import { apiClient, apiGet, apiPatch } from '@/services/apiClient';
import type { AdminStats, AdminUser, ContactMessage, UserRole } from '@/types/domain';
import type { ApiEnvelope } from '@/types/api';

interface Paged<T> {
  items: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

async function getPaged<T>(url: string, params?: object): Promise<Paged<T>> {
  const res = await apiClient.get<ApiEnvelope<T[]>>(url, { params });
  return {
    items: res.data.data,
    page: res.data.meta?.page ?? 0,
    size: res.data.meta?.size ?? res.data.data.length,
    totalElements: res.data.meta?.totalElements ?? res.data.data.length,
    totalPages: res.data.meta?.totalPages ?? 1,
  };
}

/* ----------------------------- Users ----------------------------- */

export interface ListUsersParams {
  q?: string;
  page?: number;
  size?: number;
}

export interface UpdateUserPayload {
  enabled?: boolean;
  roles?: UserRole[];
  /** Empty string clears the field on the server. */
  phoneNumber?: string;
  address?: string;
}

export const adminUsersService = {
  list(params: ListUsersParams = {}) {
    return getPaged<AdminUser>('/admin/users', params);
  },

  update(id: string, payload: UpdateUserPayload) {
    return apiPatch<AdminUser, UpdateUserPayload>(`/admin/users/${id}`, payload);
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/admin/users/${id}`);
  },
};

/* ---------------------------- Messages --------------------------- */

export interface ListContactParams {
  page?: number;
  size?: number;
}

export const adminContactService = {
  list(params: ListContactParams = {}) {
    return getPaged<ContactMessage>('/admin/contact', params);
  },

  async unreadCount(): Promise<number> {
    const res = await apiClient.get<ApiEnvelope<{ count: number }>>('/admin/contact/unread-count');
    return res.data.data.count;
  },

  setRead(id: string, read: boolean) {
    return apiPatch<ContactMessage, { read: boolean }>(`/admin/contact/${id}/read`, { read });
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/admin/contact/${id}`);
  },
};

/* ------------------------------ Stats ------------------------------ */

export const adminStatsService = {
  get(): Promise<AdminStats> {
    return apiGet<AdminStats>('/admin/stats');
  },
};
